import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { User, Mail, Phone, Briefcase, MapPin, Globe, Linkedin, Trash2, Search, Download, Edit2, X, Plus, Share2, RefreshCw } from 'lucide-react';
import SidebarNavigation from '../components/SidebarNavigation';
import { toast } from 'sonner';
import { Input } from '../components/ui/input';
import { fetchContactsFromBackend, migrateLocalStorageContacts } from '../lib/contactDownload';
import { getUser } from '../lib/auth';
import axios from 'axios';

const Contacts = () => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [editingContact, setEditingContact] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);

  const API = process.env.REACT_APP_BACKEND_URL + '/api';

  useEffect(() => {
    fetchContacts();
  }, []);

  const fetchContacts = async () => {
    try {
      const user = getUser();
      
      if (user) {
        // User is logged in - fetch from backend
        console.log('📥 Fetching contacts from backend...');
        
        // Check if we need to migrate localStorage contacts
        const migrated = localStorage.getItem('contactsMigrated');
        if (!migrated) {
          const localContacts = JSON.parse(localStorage.getItem('scannedContacts') || '[]');
          if (localContacts.length > 0) {
            console.log(`🔄 Migrating ${localContacts.length} contacts from localStorage...`);
            toast.loading('Migrating your contacts...', { id: 'migration' });
            const result = await migrateLocalStorageContacts();
            if (result.success) {
              toast.success(result.message, { id: 'migration' });
            }
          } else {
            localStorage.setItem('contactsMigrated', 'true');
          }
        }
        
        // Fetch contacts from backend
        const backendContacts = await fetchContactsFromBackend();
        setContacts(backendContacts);
        
        if (backendContacts.length === 0) {
          toast.info('No contacts yet. Scan a business card to get started!');
        }
      } else {
        // User not logged in - load from localStorage
        console.log('📥 Loading contacts from localStorage...');
        const savedContacts = JSON.parse(localStorage.getItem('scannedContacts') || '[]');
        setContacts(savedContacts);
        
        if (savedContacts.length === 0) {
          toast.info('No contacts yet. Scan a business card to get started!');
        } else {
          toast.info('💡 Login to save contacts permanently');
        }
      }
    } catch (error) {
      console.error('Error loading contacts:', error);
      toast.error('Failed to load contacts');
      setContacts([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAddSocialMedia = () => {
    setEditingContact(prev => ({
      ...prev,
      socialMedia: [...(prev.socialMedia || []), { platform: '', url: '' }]
    }));
  };

  const handleRemoveSocialMedia = (index) => {
    setEditingContact(prev => ({
      ...prev,
      socialMedia: prev.socialMedia.filter((_, i) => i !== index)
    }));
  };

  const handleSocialMediaChange = (index, field, value) => {
    setEditingContact(prev => ({
      ...prev,
      socialMedia: prev.socialMedia.map((item, i) => 
        i === index ? { ...item, [field]: value } : item
      )
    }));
  };

  const handleEdit = (contact) => {
    const contactCopy = {...contact};
    // Ensure socialMedia array exists
    if (!contactCopy.socialMedia) {
      contactCopy.socialMedia = [];
    }
    setEditingContact(contactCopy);
    setShowEditModal(true);
  };

  const handleSaveEdit = () => {
    try {
      // Update in localStorage
      const savedContacts = JSON.parse(localStorage.getItem('scannedContacts') || '[]');
      const updatedContacts = savedContacts.map(c => 
        c.id === editingContact.id ? editingContact : c
      );
      localStorage.setItem('scannedContacts', JSON.stringify(updatedContacts));
      
      setContacts(updatedContacts);
      setShowEditModal(false);
      setEditingContact(null);
      toast.success('Contact updated successfully');
    } catch (error) {
      console.error('Error updating contact:', error);
      toast.error('Failed to update contact');
    }
  };

  const handleDelete = async (contactId, contactName) => {
    if (!window.confirm(`Are you sure you want to delete ${contactName}?`)) {
      return;
    }

    try {
      const user = getUser();
      
      if (user) {
        // User is logged in - delete from backend
        const headers = { Authorization: `Bearer ${localStorage.getItem('auth_token')}` };
        await axios.delete(`${API}/contacts/${contactId}`, { headers });
        
        // Update local state
        const updatedContacts = contacts.filter(c => c.id !== contactId);
        setContacts(updatedContacts);
        toast.success('Contact deleted successfully');
      } else {
        // Delete from localStorage only
        const savedContacts = JSON.parse(localStorage.getItem('scannedContacts') || '[]');
        const updatedContacts = savedContacts.filter(c => c.id !== contactId);
        localStorage.setItem('scannedContacts', JSON.stringify(updatedContacts));
        
        setContacts(updatedContacts);
        toast.success('Contact deleted successfully');
      }
    } catch (error) {
      console.error('Error deleting contact:', error);
      toast.error('Failed to delete contact');
    }
  };

  const generateVCard = (contact) => {
    const vcard = [
      'BEGIN:VCARD',
      'VERSION:3.0',
      `FN:${contact.name || 'Unknown'}`,
      contact.businessName ? `ORG:${contact.businessName}` : '',
      contact.title ? `TITLE:${contact.title}` : '',
      contact.email ? `EMAIL:${contact.email}` : '',
      contact.phone ? `TEL;TYPE=WORK,VOICE:${contact.phone}` : '',
      contact.mobile ? `TEL;TYPE=CELL,VOICE:${contact.mobile}` : '',
      contact.website ? `URL:${contact.website}` : '',
      contact.address ? `ADR;TYPE=WORK:;;${contact.address}` : '',
      contact.linkedin ? `X-SOCIALPROFILE;TYPE=linkedin:${contact.linkedin}` : '',
      'END:VCARD'
    ].filter(line => line && !line.endsWith(':')).join('\n');
    
    return vcard;
  };

  const downloadVCard = (contact) => {
    const vcard = generateVCard(contact);
    const blob = new Blob([vcard], { type: 'text/vcard' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${contact.name?.replace(/\s+/g, '_') || 'contact'}.vcf`;
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success('vCard downloaded!');
  };

  const exportToCSV = () => {
    const headers = ['Name', 'Company', 'Title', 'Email', 'Phone', 'Mobile', 'Website', 'Address', 'LinkedIn'];
    const rows = contacts.map(c => [
      c.name || '',
      c.businessName || '',
      c.title || '',
      c.email || '',
      c.phone || '',
      c.mobile || '',
      c.website || '',
      c.address || '',
      c.linkedin || ''
    ]);

    const csv = [
      headers.join(','),
      ...rows.map(row => row.map(field => `"${field}"`).join(','))
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `contacts-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success('Contacts exported to CSV');
  };

  const filteredContacts = contacts.filter(contact => {
    const searchLower = searchTerm.toLowerCase();
    return (
      contact.name?.toLowerCase().includes(searchLower) ||
      contact.businessName?.toLowerCase().includes(searchLower) ||
      contact.email?.toLowerCase().includes(searchLower) ||
      contact.phone?.includes(searchTerm)
    );
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100">
      <SidebarNavigation />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 ml-0 sm:ml-16">
        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <h1 className="text-4xl font-bold text-slate-900 mb-2" style={{fontFamily: 'Space Grotesk, sans-serif'}}>
                My Contacts
              </h1>
              <p className="text-slate-600">
                {contacts.length} contact{contacts.length !== 1 ? 's' : ''} saved on this device
              </p>
            </div>
            <div className="flex gap-2">
              <Button
                onClick={() => window.location.href = '/contact-groups'}
                className="bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600 text-white"
              >
                📱 Contact Groups
              </Button>
              <Button
                onClick={exportToCSV}
                variant="outline"
                disabled={contacts.length === 0}
              >
                <Download className="w-4 h-4 mr-2" />
                Export CSV
              </Button>
            </div>
          </div>
        </div>

        {/* Search */}
        <div className="mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
            <Input
              type="text"
              placeholder="Search by name, company, email, or phone..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : filteredContacts.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <div className="flex flex-col items-center gap-4">
                <div className="w-20 h-20 bg-slate-100 rounded-full flex items-center justify-center">
                  <User className="w-10 h-10 text-slate-400" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-slate-900 mb-2">
                    {searchTerm ? 'No contacts found' : 'No contacts yet'}
                  </h3>
                  <p className="text-slate-600">
                    {searchTerm 
                      ? 'Try adjusting your search term' 
                      : 'Scan business cards to save contacts on this device. No login required!'
                    }
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredContacts.map((contact) => (
              <Card key={contact.id} className="hover:shadow-lg transition-shadow">
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-purple-600 rounded-full flex items-center justify-center text-white font-bold text-lg">
                        {contact.name?.charAt(0).toUpperCase() || '?'}
                      </div>
                      <div>
                        <CardTitle className="text-lg">{contact.name || 'Unknown'}</CardTitle>
                        {contact.title && (
                          <p className="text-sm text-slate-600">{contact.title}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEdit(contact)}
                        className="text-green-600 hover:text-green-700 hover:bg-green-50"
                        title="Edit contact"
                      >
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => downloadVCard(contact)}
                        className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                        title="Download vCard"
                      >
                        <Download className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(contact.id, contact.name)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        title="Delete contact"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-2">
                  {contact.businessName && (
                    <div className="flex items-start gap-2">
                      <Briefcase className="w-4 h-4 text-slate-500 mt-0.5 flex-shrink-0" />
                      <span className="text-sm text-slate-700">{contact.businessName}</span>
                    </div>
                  )}
                  {contact.email && (
                    <div className="flex items-start gap-2">
                      <Mail className="w-4 h-4 text-slate-500 mt-0.5 flex-shrink-0" />
                      <a href={`mailto:${contact.email}`} className="text-sm text-blue-600 hover:underline break-all">
                        {contact.email}
                      </a>
                    </div>
                  )}
                  {contact.phone && (
                    <div className="flex items-start gap-2">
                      <Phone className="w-4 h-4 text-slate-500 mt-0.5 flex-shrink-0" />
                      <a href={`tel:${contact.phone}`} className="text-sm text-slate-700 hover:text-blue-600">
                        {contact.phone}
                      </a>
                    </div>
                  )}
                  {contact.website && (
                    <div className="flex items-start gap-2">
                      <Globe className="w-4 h-4 text-slate-500 mt-0.5 flex-shrink-0" />
                      <a 
                        href={contact.website.startsWith('http') ? contact.website : `https://${contact.website}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-blue-600 hover:underline break-all"
                      >
                        {contact.website}
                      </a>
                    </div>
                  )}
                  {contact.address && (
                    <div className="flex items-start gap-2">
                      <MapPin className="w-4 h-4 text-slate-500 mt-0.5 flex-shrink-0" />
                      <span className="text-sm text-slate-700">{contact.address}</span>
                    </div>
                  )}
                  {contact.linkedin && (
                    <div className="flex items-start gap-2">
                      <Linkedin className="w-4 h-4 text-slate-500 mt-0.5 flex-shrink-0" />
                      <a 
                        href={contact.linkedin.startsWith('http') ? contact.linkedin : `https://${contact.linkedin}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-blue-600 hover:underline break-all"
                      >
                        LinkedIn
                      </a>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Edit Contact Modal */}
        {showEditModal && editingContact && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
                <h2 className="text-2xl font-bold text-slate-900">Edit Contact</h2>
                <button 
                  onClick={() => {
                    setShowEditModal(false);
                    setEditingContact(null);
                  }}
                  className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                >
                  <X className="w-6 h-6 text-slate-600" />
                </button>
              </div>

              <div className="p-6 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Full Name *</label>
                    <Input
                      type="text"
                      value={editingContact.name || ''}
                      onChange={(e) => setEditingContact({...editingContact, name: e.target.value})}
                      placeholder="John Doe"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Job Title</label>
                    <Input
                      type="text"
                      value={editingContact.title || ''}
                      onChange={(e) => setEditingContact({...editingContact, title: e.target.value})}
                      placeholder="CEO, Manager, etc."
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Company Name</label>
                    <Input
                      type="text"
                      value={editingContact.businessName || ''}
                      onChange={(e) => setEditingContact({...editingContact, businessName: e.target.value})}
                      placeholder="Company Inc."
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
                    <Input
                      type="email"
                      value={editingContact.email || ''}
                      onChange={(e) => setEditingContact({...editingContact, email: e.target.value})}
                      placeholder="john@example.com"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Phone</label>
                    <Input
                      type="tel"
                      value={editingContact.phone || ''}
                      onChange={(e) => setEditingContact({...editingContact, phone: e.target.value})}
                      placeholder="+1 234 567 8900"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Mobile</label>
                    <Input
                      type="tel"
                      value={editingContact.mobile || ''}
                      onChange={(e) => setEditingContact({...editingContact, mobile: e.target.value})}
                      placeholder="+1 234 567 8900"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Website</label>
                    <Input
                      type="url"
                      value={editingContact.website || ''}
                      onChange={(e) => setEditingContact({...editingContact, website: e.target.value})}
                      placeholder="https://example.com"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">LinkedIn</label>
                    <Input
                      type="text"
                      value={editingContact.linkedin || ''}
                      onChange={(e) => setEditingContact({...editingContact, linkedin: e.target.value})}
                      placeholder="linkedin.com/in/username"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Address</label>
                  <textarea
                    value={editingContact.address || ''}
                    onChange={(e) => setEditingContact({...editingContact, address: e.target.value})}
                    rows={3}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="123 Main St, City, State, ZIP"
                  />
                </div>

                {/* Social Media Links Section */}
                <div className="border-t border-gray-200 pt-4">
                  <div className="flex items-center justify-between mb-3">
                    <label className="block text-sm font-medium text-gray-700">Social Media Links</label>
                    <button
                      type="button"
                      onClick={handleAddSocialMedia}
                      className="flex items-center gap-1 px-3 py-1 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      <Plus className="w-4 h-4" />
                      Add Link
                    </button>
                  </div>
                  
                  {editingContact.socialMedia && editingContact.socialMedia.length > 0 && (
                    <div className="space-y-3">
                      {editingContact.socialMedia.map((social, index) => (
                        <div key={index} className="flex gap-2">
                          <select
                            value={social.platform}
                            onChange={(e) => handleSocialMediaChange(index, 'platform', e.target.value)}
                            className="w-1/3 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          >
                            <option value="">Select Platform</option>
                            <option value="Facebook">Facebook</option>
                            <option value="Instagram">Instagram</option>
                            <option value="Twitter">Twitter/X</option>
                            <option value="TikTok">TikTok</option>
                            <option value="YouTube">YouTube</option>
                            <option value="Pinterest">Pinterest</option>
                            <option value="Snapchat">Snapchat</option>
                            <option value="WhatsApp">WhatsApp</option>
                            <option value="Telegram">Telegram</option>
                            <option value="Discord">Discord</option>
                            <option value="Reddit">Reddit</option>
                            <option value="Other">Other</option>
                          </select>
                          <input
                            type="url"
                            value={social.url}
                            onChange={(e) => handleSocialMediaChange(index, 'url', e.target.value)}
                            placeholder="https://..."
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          />
                          <button
                            type="button"
                            onClick={() => handleRemoveSocialMedia(index)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          >
                            <X className="w-5 h-5" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="flex gap-3 pt-4">
                  <Button
                    onClick={() => {
                      setShowEditModal(false);
                      setEditingContact(null);
                    }}
                    variant="outline"
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleSaveEdit}
                    className="flex-1 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white"
                  >
                    Save Changes
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Contacts;
